import React, { useState } from 'react';
import styles from './styles.module.scss';

type ISkeletonProps = React.ComponentPropsWithoutRef<'div'> & {
  variant?: 'sharp' | 'circular' | 'rounded';
  isLoaded?: boolean;
};

const Skeleton = React.forwardRef<HTMLDivElement, ISkeletonProps>(
  (
    {
      className,
      variant = 'rounded',
      children,
      isLoaded = false,
      ...props
    },
    ref
  ) => {
    if (!isLoaded) {
      return (
        <div
          ref={ref}
          className={`${styles.skeleton} ${styles[variant]} ${className}`}
          {...props}
        />
      );
    } else {
      return <>{children}</>;
    }
  }
);

type ISkeletonTextProps = React.ComponentPropsWithoutRef<'div'> & {
  _lines?: number;
  isLoaded?: boolean;
};

const SkeletonText = React.forwardRef<HTMLDivElement, ISkeletonTextProps>(
  (
    {
      className,
      _lines,
      isLoaded = false,
      children,
      ...props
    },
    ref
  ) => {
    if (!isLoaded) {
      if (_lines) {
        return (
          <div
            ref={ref}
            className={styles.skeletonTextContainer}
          >
            {Array.from({ length: _lines }).map((_, index) => (
              <div
                key={index}
                className={`${styles.skeleton} ${className}`}
                {...props}
              />
            ))}
          </div>
        );
      } else {
        return (
          <div
            ref={ref}
            className={`${styles.skeleton} ${className}`}
            {...props}
          />
        );
      }
    } else {
      return <>{children}</>;
    }
  }
);

type ISkeletonImageProps = React.ComponentPropsWithoutRef<'img'> & {
  variant?: 'sharp' | 'circular' | 'rounded';
};

const SkeletonImage = React.forwardRef<HTMLImageElement, ISkeletonImageProps>(
  (
    {
      className,
      variant = 'rounded',
      src,
      alt,
      ...props
    },
    ref
  ) => {
    const [isLoaded, setIsLoaded] = useState(false);

    return (
      <>
        <img
          ref={ref}
          src={src}
          alt={alt}
          className={`${className} ${isLoaded ? '' : styles.hidden}`}
          onLoad={() => setIsLoaded(true)}
          {...props}
        />
        {!isLoaded && (
          <Skeleton
            variant={variant}
            className={className}
          />
        )}
      </>
    );
  }
);

Skeleton.displayName = 'Skeleton';
SkeletonText.displayName = 'SkeletonText';
SkeletonImage.displayName = 'SkeletonImage';

export { Skeleton, SkeletonText, SkeletonImage };
